// Génère une image PNG partageable (carré 1080x1080) depuis le verdict, via Canvas.
// Structure pensée pour attirer le spectateur : menace en haut, note au centre, appel à l'action.

export const TONE_HEX = {
  a: '#3ee6a0',
  b: '#19e6d2',
  c: '#ffb020',
  d: '#ff8a3c',
  f: '#ff3d5e',
  danger: '#ffb020',
};

const BG = '#06090f';
const TEXT = '#e9f2f8';
const MUTED = '#8595a8';
const DIM = '#566274';

const DISPLAY = '"Chakra Petch", "Rajdhani", sans-serif';
const MONO = '"JetBrains Mono", ui-monospace, monospace';

async function ensureFonts() {
  if (!document.fonts) return;
  try {
    await Promise.all([
      document.fonts.load('800 200px "Chakra Petch"'),
      document.fonts.load('700 60px "Chakra Petch"'),
      document.fonts.load('800 200px "JetBrains Mono"'),
      document.fonts.ready,
    ]);
  } catch (_) {
    /* fallback système */
  }
}

function loadImage(src) {
  return new Promise((resolve) => {
    if (!src) {
      resolve(null);
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function wrapText(ctx, text, maxWidth) {
  const words = (text || '').split(' ');
  const lines = [];
  let line = '';
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

// "100 / 100" centré, retourne la hauteur consommée.
function drawScore(ctx, cx, top, score) {
  const numTxt = `${score}`;
  ctx.textBaseline = 'alphabetic';
  ctx.font = `800 86px ${MONO}`;
  const numW = ctx.measureText(numTxt).width;
  ctx.font = `700 46px ${MONO}`;
  const suf = ' / 100';
  const sufW = ctx.measureText(suf).width;
  const startX = cx - (numW + sufW) / 2;
  const baseline = top + 76;
  ctx.textAlign = 'left';
  ctx.fillStyle = TEXT;
  ctx.font = `800 86px ${MONO}`;
  ctx.fillText(numTxt, startX, baseline);
  ctx.fillStyle = DIM;
  ctx.font = `700 46px ${MONO}`;
  ctx.fillText(suf, startX + numW, baseline);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  return 104;
}

// "7 / 20" géant (mode danger).
function drawBigNumber(ctx, cx, top, flagged, total, tone) {
  const num = `${flagged}`;
  ctx.textBaseline = 'alphabetic';
  ctx.font = `800 220px ${MONO}`;
  const numW = ctx.measureText(num).width;
  ctx.font = `700 96px ${MONO}`;
  const suf = ` / ${total}`;
  const sufW = ctx.measureText(suf).width;
  const startX = cx - (numW + sufW) / 2;
  const baseline = top + 198;
  ctx.textAlign = 'left';
  ctx.fillStyle = tone;
  ctx.font = `800 220px ${MONO}`;
  ctx.shadowColor = hexA(tone, 0.5);
  ctx.shadowBlur = 50;
  ctx.fillText(num, startX, baseline);
  ctx.shadowBlur = 0;
  ctx.fillStyle = DIM;
  ctx.font = `700 96px ${MONO}`;
  ctx.fillText(suf, startX + numW, baseline);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  return 250;
}

export async function buildShareImage({ mode, toneHex, logoSrc, grade, score, flagged, total, eyebrow, line, sub, url }) {
  await ensureFonts();
  const logo = await loadImage(logoSrc);

  const S = 1080;
  const canvas = document.createElement('canvas');
  canvas.width = S;
  canvas.height = S;
  const ctx = canvas.getContext('2d');
  const tone = toneHex || TONE_HEX.danger;
  const cx = S / 2;

  // --- Fond ---
  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, S, S);
  const glow = ctx.createRadialGradient(cx, 150, 40, cx, 150, 780);
  glow.addColorStop(0, hexA(tone, 0.16));
  glow.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, S, S);
  ctx.strokeStyle = 'rgba(120,210,220,0.05)';
  ctx.lineWidth = 1;
  for (let x = 0; x <= S; x += 72) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, S); ctx.stroke(); }
  for (let yy = 0; yy <= S; yy += 72) { ctx.beginPath(); ctx.moveTo(0, yy); ctx.lineTo(S, yy); ctx.stroke(); }
  ctx.strokeStyle = hexA(tone, 0.35);
  ctx.lineWidth = 2;
  ctx.strokeRect(40, 40, S - 80, S - 80);
  ctx.fillStyle = tone;
  ctx.fillRect(40, 40, S - 80, 6);

  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  let y = 108;

  // --- Logo ---
  if (logo && logo.width) {
    const lw = 430;
    const lh = logo.height * (lw / logo.width);
    ctx.drawImage(logo, cx - lw / 2, y, lw, lh);
    y += lh + 48;
  } else {
    ctx.fillStyle = tone;
    ctx.font = `700 48px ${DISPLAY}`;
    ctx.fillText('KEEPMYGAMES', cx, y);
    y += 84;
  }

  if (mode === 'score') {
    // --- Étiquette : le nombre est une NOTE (évite de le lire comme un compte) ---
    ctx.fillStyle = DIM;
    ctx.font = `600 34px ${MONO}`;
    ctx.fillText(`// ${(eyebrow || '').toUpperCase()}`, cx, y);
    y += 74;

    // --- Grande note ---
    ctx.fillStyle = tone;
    ctx.font = `800 240px ${DISPLAY}`;
    ctx.shadowColor = tone;
    ctx.shadowBlur = 55;
    ctx.fillText(grade, cx, y);
    ctx.shadowBlur = 0;
    y += 256;

    // --- Score ---
    y += drawScore(ctx, cx, y, score);
    y += 58;

    // --- Défi (comparaison, visé sur le spectateur) ---
    ctx.fillStyle = TEXT;
    ctx.font = `800 50px ${DISPLAY}`;
    for (const l of wrapText(ctx, (sub || '').toUpperCase(), S - 150)) {
      ctx.fillText(l, cx, y);
      y += 60;
    }
  } else {
    // --- Chiffre géant + phrase (danger) ---
    y += drawBigNumber(ctx, cx, y, flagged, total, tone);
    y += 20;
    ctx.fillStyle = TEXT;
    ctx.font = `800 56px ${DISPLAY}`;
    for (const l of wrapText(ctx, (line || '').toUpperCase(), S - 160)) {
      ctx.fillText(l, cx, y);
      y += 66;
    }
    y += 18;
    ctx.fillStyle = MUTED;
    ctx.font = `500 42px ${DISPLAY}`;
    ctx.fillText(sub || '', cx, y);
  }

  // --- URL en bas ---
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = tone;
  ctx.font = `700 40px ${MONO}`;
  ctx.fillText(url || 'keepmygames.com', cx, S - 76);

  return await new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/png'));
}

// "#rrggbb" -> "rgba(r,g,b,a)"
function hexA(hex, a) {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}
